import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Landscape,
  Home,
  Storefront,
  House,
  Apartment,
  BusinessCenter,
  Villa,
  HolidayVillage,
  OtherHouses,
  Close as CloseIcon,
  CheckCircle,
  // Highlights icons
  Security,
  FitnessCenter,
  SportsBasketball,
  DirectionsRun,
  ChildCare,
  Park,
  SelfImprovement,
  WorkspacePremium,
  SportsScore,
  Videocam,
  BeachAccess,
  Forest,
  Pool,
  Lock,
  LocationCity,
  DirectionsCar,
  Description,
  // Amenities icons
  Theaters,
  Celebration,
  LocalLibrary,
  LocalCafe,
  Hotel,
  DirectionsBike,
  Spa,
  SportsTennis,
  SportsCricket,
  SportsSoccer,
  SportsVolleyball,
  RollerSkating,
  Doorbell,
  Phone,
  DoorFront,
  People,
  LocalFireDepartment,
  ExitToApp,
  Bolt,
  WaterDrop,
  Business,
  DeleteOutline,
  WbSunny,
  EvStation,
  LocalGasStation,
  Elevator,
  SupportAgent,
  Chair,
  Build,
  CleaningServices,
  Deck,
  ChildFriendly,
  LocalPharmacy,
  LocalAtm,
  ContentCut,
  LocalLaundryService,
  LocalHospital,
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { styled } from '@mui/material/styles';

import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';
import { GOOGLEMAPS_API_KEY, BASE_URL } from '../../Api/ApiUrls';
import { checkPropertyUploadPermission } from "./subscriptionCheck";
import SubscriptionModal from "./SubscriptionModal";
import GuestLoginModal from '../../GuestLoginModal';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
import FullScreenLoader from '../../FullScreenLoader';

import { GoogleMap, useLoadScript, Autocomplete } from "@react-google-maps/api";
import { Landmark } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

// ============================================================
// Styled Components
// ============================================================
const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh",
}));

const containerStyle = {
  width: "100%",
  height: "300px",
  borderRadius: "12px",
  overflow: "hidden",
};

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B4513',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': { backgroundColor: '#654321' },
}));

// ✅ Moved OUTSIDE the component (defining a styled component inside render
// re-creates it on every render and remounts the file input).
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// ============================================================
// Constants
// ============================================================
const LOOKING_FOR_OPTIONS = ['JV/JD', 'Build to Suit'];

const categoryIcons = {
  'JV/JD': (
    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F5EAD8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <BusinessCenter sx={{ color: '#8B6914', fontSize: 20 }} />
    </Box>
  ),
  'Build to Suit': (
    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F5EAD8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Apartment sx={{ color: '#8B6914', fontSize: 20 }} />
    </Box>
  ),
  'Land': (
    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F5EAD8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Landscape sx={{ color: '#8B6914', fontSize: 20 }} />
    </Box>
  ),
  'Plot': (
    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F5EAD8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Home sx={{ color: '#8B6914', fontSize: 20 }} />
    </Box>
  ),
};


const PROPERTY_TYPE_OPTIONS = {
  'JV/JD': [
    'Land',
    'Plot',
    'Layouts',
    'Villas',
    'Apartments',
    'Row Houses',
    'Retail/Showrooms',
    'Hotels',
    'Restaurants',
    'Schools',
    'Colleges',
    'Mixed-use Building',
    'Shopping Complex',
    'Hospital'
  ],

  'Build to Suit': [
    'Land',
    'Plot',
    'Layouts',
    'Villas',
    'Duplex House',
    'Apartments',
    'Farm House',
    'Independent House',
    'Office',
    'Co-working Space',
    'Row Houses',
    'Shopping Complex',
    'Retail/Showrooms',
    'Hotels',
    'Resorts',
    'Hospital',
    'Restaurants',
    'Schools',
    'Colleges',
    'Training Centers',
    'Mixed-use Building',
    'Parking Facility',
    'Sports Facility',
    'Warehouse',
    'Fuel Station',
    'Logistics',
    'Factories',
    'Manufacturing Units',
    'Industrial Shed'
  ]
};

// Purchase, JV/JD and Build to Suit all use the same budget list,
// so it is defined once here and reused below.
// (If you ever need different ranges for one of them, copy this list and edit it.)
const PURCHASE_STYLE_RANGES = [
  { label: '5L - 10L', min: 500000, max: 1000000 },
  { label: '10L - 20L', min: 1000000, max: 2000000 },
  { label: '20L - 30L', min: 2000000, max: 3000000 },
  { label: '30L - 40L', min: 3000000, max: 4000000 },
  { label: '40L - 50L', min: 4000000, max: 5000000 },
  { label: '50L - 60L', min: 5000000, max: 6000000 },
  { label: '60L - 70L', min: 6000000, max: 7000000 },
  { label: '70L - 80L', min: 7000000, max: 8000000 },
  { label: '80L - 90L', min: 8000000, max: 9000000 },
  { label: '90L - 1CR', min: 9000000, max: 10000000 },

  { label: '1CR - 1.25CR', min: 10000000, max: 12500000 },
  { label: '1.25CR - 1.5CR', min: 12500000, max: 15000000 },
  { label: '1.5CR - 1.75CR', min: 15000000, max: 17500000 },
  { label: '1.75CR - 2CR', min: 17500000, max: 20000000 },
  { label: '2CR - 2.5CR', min: 20000000, max: 25000000 },
  { label: '2.5CR - 3CR', min: 25000000, max: 30000000 },
  { label: '3CR - 3.5CR', min: 30000000, max: 35000000 },
  { label: '3.5CR - 4CR', min: 35000000, max: 40000000 },
  { label: '4CR - 4.5CR', min: 40000000, max: 45000000 },
  { label: '4.5CR - 5CR', min: 45000000, max: 50000000 },
  { label: '5CR - 5.5CR', min: 50000000, max: 55000000 },
  { label: '5.5CR - 6CR', min: 55000000, max: 60000000 },
  { label: '6CR - 8CR', min: 60000000, max: 80000000 },
  { label: '8CR - 9CR', min: 80000000, max: 90000000 },
  { label: '9CR - 10CR', min: 90000000, max: 100000000 },

  { label: '10CR - 15CR', min: 100000000, max: 150000000 },
  { label: '15CR - 20CR', min: 150000000, max: 200000000 },
  { label: '20CR - 25CR', min: 200000000, max: 250000000 },
  { label: '25CR - 30CR', min: 250000000, max: 300000000 },
  { label: '30CR - 40CR', min: 300000000, max: 400000000 },
  { label: '40CR - 50CR', min: 400000000, max: 500000000 },
  { label: '50CR - 60CR', min: 500000000, max: 600000000 },
  { label: '60CR - 70CR', min: 600000000, max: 700000000 },
  { label: '70CR - 80CR', min: 700000000, max: 800000000 },
  { label: '80CR - 90CR', min: 800000000, max: 900000000 },
  { label: '90CR - 100CR', min: 900000000, max: 1000000000 },

  { label: '100CR - 120CR', min: 1000000000, max: 1200000000 },
  { label: '120CR - 140CR', min: 1200000000, max: 1400000000 },
  { label: '140CR - 160CR', min: 1400000000, max: 1600000000 },
  { label: '160CR - 190CR', min: 1600000000, max: 1900000000 },
  { label: '190CR - 200CR', min: 1900000000, max: 2000000000 },
  { label: '200CR - 250CR', min: 2000000000, max: 2500000000 },
  { label: '250CR - 300CR', min: 2500000000, max: 3000000000 },
  { label: '300CR - 350CR', min: 3000000000, max: 3500000000 },
  { label: '350CR - 400CR', min: 3500000000, max: 4000000000 },
  { label: '400CR - 450CR', min: 4000000000, max: 4500000000 },
  { label: '450CR - 500CR', min: 4500000000, max: 5000000000 },
  { label: '500CR - 550CR', min: 5000000000, max: 5500000000 },
  { label: '550CR - 600CR', min: 5500000000, max: 6000000000 },
  { label: '600CR - 650CR', min: 6000000000, max: 6500000000 },
  { label: '650CR - 700CR', min: 6500000000, max: 7000000000 },
  { label: '700CR - 750CR', min: 7000000000, max: 7500000000 },
  { label: '750CR - 800CR', min: 7500000000, max: 8000000000 },
  { label: '800CR - 850CR', min: 8000000000, max: 8500000000 },
  { label: '850CR - 900CR', min: 8500000000, max: 9000000000 },
  { label: '900CR - 1000CR', min: 9000000000, max: 10000000000 },

  { label: '1000CR - 1500CR', min: 10000000000, max: 15000000000 },
  { label: '1500CR - 2000CR', min: 15000000000, max: 20000000000 },
  { label: '2000CR - 2500CR', min: 20000000000, max: 25000000000 },
  { label: '2500CR - 3000CR', min: 25000000000, max: 30000000000 },
  { label: '3000CR - 4000CR', min: 30000000000, max: 40000000000 },
  { label: '4000CR - 5000CR', min: 40000000000, max: 50000000000 },
  { label: '5000CR - 6000CR', min: 50000000000, max: 60000000000 },
  { label: '6000CR - 7000CR', min: 60000000000, max: 70000000000 },
  { label: '7000CR - 8000CR', min: 70000000000, max: 80000000000 },
  { label: '8000CR - 9000CR', min: 80000000000, max: 90000000000 },
  { label: '9000CR - 10000CR+++', min: 90000000000, max: 100000000000 },
];

const BUDGET_RANGES = {
  Purchase: PURCHASE_STYLE_RANGES,

  Rent: [
    { label: '1K - 2K', min: 1000, max: 2000 },
    { label: '2K - 3K', min: 2000, max: 3000 },
    { label: '3K - 4K', min: 3000, max: 4000 },
    { label: '4K - 5K', min: 4000, max: 5000 },
    { label: '5K - 10K', min: 5000, max: 10000 },
    { label: '10K - 15K', min: 10000, max: 15000 },
    { label: '15K - 20K', min: 15000, max: 20000 },
    { label: '20K - 25K', min: 20000, max: 25000 },
    { label: '25K - 30K', min: 25000, max: 30000 },
    { label: '30K - 35K', min: 30000, max: 35000 },
    { label: '35K - 40K', min: 35000, max: 40000 },
    { label: '40K - 50K', min: 40000, max: 50000 },
    { label: '50K - 60K', min: 50000, max: 60000 },
    { label: '60K - 70K', min: 60000, max: 70000 },
    { label: '70K - 80K', min: 70000, max: 80000 },
    { label: '80K - 90K', min: 80000, max: 90000 },
    { label: '90K - 1L', min: 90000, max: 100000 },
    { label: '1L - 1.25L', min: 100000, max: 125000 },
    { label: '1.25L - 1.5L', min: 125000, max: 150000 },
    { label: '1.5L - 2L', min: 150000, max: 200000 },
    { label: '2L - 2.5L', min: 200000, max: 250000 },
    { label: '2.5L - 3L', min: 250000, max: 300000 },
    { label: '3L - 3.5L', min: 300000, max: 350000 },
    { label: '3.5L - 4L', min: 350000, max: 400000 },
    { label: '4L - 4.5L', min: 400000, max: 450000 },
    { label: '4.5L - 5L', min: 450000, max: 500000 },
    { label: '5L - 6L', min: 500000, max: 600000 },
    { label: '6L - 7L', min: 600000, max: 700000 },
    { label: '7L - 8L', min: 700000, max: 800000 },
    { label: '8L - 9L', min: 800000, max: 900000 },
    { label: '9L - 10L', min: 900000, max: 1000000 },
    { label: '10L - 15L', min: 1000000, max: 1500000 },
    { label: '15L - 20L', min: 1500000, max: 2000000 },
    { label: '20L - 25L', min: 2000000, max: 2500000 },
    { label: '25L - 30L', min: 2500000, max: 3000000 },
    { label: '30L+++', min: 3000000, max: 999999999999 },
  ],

  Lease: [
    { label: '1L - 2L', min: 100000, max: 200000 },
    { label: '2L - 3L', min: 200000, max: 300000 },
    { label: '3L - 4L', min: 300000, max: 400000 },
    { label: '4L - 5L', min: 400000, max: 500000 },
    { label: '5L - 10L', min: 500000, max: 1000000 },
    { label: '10L - 15L', min: 1000000, max: 1500000 },
    { label: '15L - 20L', min: 1500000, max: 2000000 },
    { label: '20L - 25L', min: 2000000, max: 2500000 },
    { label: '25L - 30L', min: 2500000, max: 3000000 },
    { label: '30L - 35L', min: 3000000, max: 3500000 },
    { label: '35L - 40L', min: 3500000, max: 4000000 },
    { label: '40L - 50L', min: 4000000, max: 5000000 },
    { label: '50L - 60L', min: 5000000, max: 6000000 },
    { label: '60L - 70L', min: 6000000, max: 7000000 },
    { label: '70L - 80L', min: 7000000, max: 8000000 },
    { label: '80L - 90L', min: 8000000, max: 9000000 },
    { label: '90L - 1CR', min: 9000000, max: 10000000 },
    { label: '1CR - 1.25CR', min: 10000000, max: 12500000 },
    { label: '1.25CR - 1.5CR', min: 12500000, max: 15000000 },
    { label: '1.5CR - 2CR', min: 15000000, max: 20000000 },
    { label: '2CR - 2.5CR', min: 20000000, max: 25000000 },
    { label: '2.5CR - 3CR', min: 25000000, max: 30000000 },
    { label: '3CR - 3.5CR', min: 30000000, max: 35000000 },
    { label: '3.5CR - 4CR', min: 35000000, max: 40000000 },
    { label: '4CR - 4.5CR', min: 40000000, max: 45000000 },
    { label: '4.5CR - 5CR', min: 45000000, max: 50000000 },
    { label: '5CR - 6CR', min: 50000000, max: 60000000 },
    { label: '6CR - 7CR', min: 60000000, max: 70000000 },
    { label: '7CR - 8CR', min: 70000000, max: 80000000 },
    { label: '8CR - 9CR', min: 80000000, max: 90000000 },
    { label: '9CR - 10CR', min: 90000000, max: 100000000 },
    { label: '10CR - 15CR', min: 100000000, max: 150000000 },
    { label: '15CR - 20CR', min: 150000000, max: 200000000 },
    { label: '20CR - 25CR', min: 200000000, max: 250000000 },
    { label: '25CR - 30CR', min: 250000000, max: 300000000 },
    { label: '30CR+++', min: 300000000, max: 999999999999 },
  ],

  'JV/JD': PURCHASE_STYLE_RANGES,

  'Build to Suit': PURCHASE_STYLE_RANGES,
};

const INITIAL_FORM_DATA = {
  length: '', width: '', units: 'ft', area: '', no_of_bedrooms: '',
  min_monthly_rent: '', max_monthly_rent: '', min_lease_per_year: '', max_lease_per_year: '',
  min_acres: '', max_acres: '', budget_per_acre: '', ratio: '', floor: '', expected_rental_income: '',
  posted_by: '', roadwidth: '', facing: '', description: '',
  highlights: [], amenities: [], landmark: ''
};

const NUMERIC_ONLY_FIELDS = [
  'length', 'width', 'area',
  'min_acres', 'max_acres', 'budget_per_acre',
  'floor', 'roadwidth',
  'min_monthly_rent', 'max_monthly_rent',
  'min_lease_per_year', 'max_lease_per_year',
  'expected_rental_income',
];

const RATIO_FIELDS = ['ratio'];

// ============================================================
// Highlights + Amenities option lists
// ============================================================
const highlightOptions = [
  { label: "24-7 Security", icon: <Security /> },
  { label: "Power Backup", icon: <Security /> },
  { label: "Gym", icon: <FitnessCenter /> },
  { label: "Indoor Games", icon: <SportsBasketball /> },
  { label: "Jogging Track", icon: <DirectionsRun /> },
  { label: "Children Play Area", icon: <ChildCare /> },
  { label: "Senior Citizen Park", icon: <Park /> },
  { label: "Yoga Deck", icon: <SelfImprovement /> },
  { label: "Multipurpose Club Hall", icon: <WorkspacePremium /> },
  { label: "Mini Theatre", icon: <SportsScore /> },
  { label: "Co-working Space", icon: <BusinessCenter /> },
  { label: "EV Charging", icon: <Videocam /> },
  { label: "Visitor Parking", icon: <Storefront /> },
  { label: "Rain Water Harvesting", icon: <BeachAccess /> },
  { label: "Landscape Garden", icon: <Forest /> },
  { label: "CCTV", icon: <Videocam /> },
  { label: "Club House", icon: <Home /> },
  { label: "Swimming Pool", icon: <Pool /> },
  { label: "Gated Community", subtitle: "24/7 Security", icon: <Lock /> },
  { label: "2, 3 & 4 BHK", subtitle: "Configurations", icon: <Apartment /> },
  { label: "High Rise", subtitle: "Premium Living", icon: <LocationCity /> },
  { label: "Covered Parking", subtitle: "1 & 2 Cars", icon: <DirectionsCar /> },
  { label: "Swimming Pool", subtitle: "With Kids Pool", icon: <Pool /> },
  { label: "RERA Registered", subtitle: "Verified Project", icon: <Description /> },
];

const amenitiesOptions = [
  // Recreation
  { label: "Swimming Pool", category: "Recreation", icon: <Pool /> },
  { label: "Kids Pool", category: "Recreation", icon: <Pool /> },
  { label: "Club House", category: "Recreation", icon: <Home /> },
  { label: "Indoor Games", category: "Recreation", icon: <SportsBasketball /> },
  { label: "Mini Theatre", category: "Recreation", icon: <Theaters /> },
  { label: "Party Hall", category: "Recreation", icon: <Celebration /> },
  { label: "Library", category: "Recreation", icon: <LocalLibrary /> },
  { label: "Cafe", category: "Recreation", icon: <LocalCafe /> },
  { label: "Guest Rooms", category: "Recreation", icon: <Hotel /> },
  // Fitness & Wellness
  { label: "Gym", category: "Fitness & Wellness", icon: <FitnessCenter /> },
  { label: "Yoga Room", category: "Fitness & Wellness", icon: <SelfImprovement /> },
  { label: "Jogging Track", category: "Fitness & Wellness", icon: <DirectionsRun /> },
  { label: "Cycling Track", category: "Fitness & Wellness", icon: <DirectionsBike /> },
  { label: "Spa", category: "Fitness & Wellness", icon: <Spa /> },
  { label: "Sauna", category: "Fitness & Wellness", icon: <Spa /> },
  { label: "Steam Room", category: "Fitness & Wellness", icon: <Spa /> },
  { label: "Meditation Area", category: "Fitness & Wellness", icon: <SelfImprovement /> },
  { label: "Fitness Centre", category: "Fitness & Wellness", icon: <FitnessCenter /> },
  // Sports
  { label: "Tennis Court", category: "Sports", icon: <SportsTennis /> },
  { label: "Basketball Court", category: "Sports", icon: <SportsBasketball /> },
  { label: "Badminton Court", category: "Sports", icon: <SportsTennis /> },
  { label: "Cricket Net", category: "Sports", icon: <SportsCricket /> },
  { label: "Football Field", category: "Sports", icon: <SportsSoccer /> },
  { label: "Table Tennis", category: "Sports", icon: <SportsTennis /> },
  { label: "Volleyball Court", category: "Sports", icon: <SportsVolleyball /> },
  { label: "Skating Area", category: "Sports", icon: <RollerSkating /> },
  { label: "Kids Play Area", category: "Sports", icon: <ChildCare /> },
  // Security
  { label: "24x7 Security", category: "Security", icon: <Security /> },
  { label: "CCTV Surveillance", category: "Security", icon: <Videocam /> },
  { label: "Video Door Phone", category: "Security", icon: <Doorbell /> },
  { label: "Intercom", category: "Security", icon: <Phone /> },
  { label: "Gated Entry", category: "Security", icon: <DoorFront /> },
  { label: "Visitor Mgmt", category: "Security", icon: <People /> },
  { label: "Fire Safety", category: "Security", icon: <LocalFireDepartment /> },
  { label: "Access Control", category: "Security", icon: <Lock /> },
  { label: "Emergency Exit", category: "Security", icon: <ExitToApp /> },
  // Utilities
  { label: "Power Backup", category: "Utilities", icon: <Bolt /> },
  { label: "Water Supply", category: "Utilities", icon: <WaterDrop /> },
  { label: "Rainwater Harvesting", category: "Utilities", icon: <WaterDrop /> },
  { label: "Sewage Treatment", category: "Utilities", icon: <Business /> },
  { label: "Waste Mgmt", category: "Utilities", icon: <DeleteOutline /> },
  { label: "Solar Power", category: "Utilities", icon: <WbSunny /> },
  { label: "EV Charging", category: "Utilities", icon: <EvStation /> },
  { label: "Gas Pipeline", category: "Utilities", icon: <LocalGasStation /> },
  { label: "STP / WTP", category: "Utilities", icon: <Business /> },
  // Building Facilities
  { label: "High Speed Lift", category: "Building Facilities", icon: <Elevator /> },
  { label: "Lobby", category: "Building Facilities", icon: <Business /> },
  { label: "Reception", category: "Building Facilities", icon: <SupportAgent /> },
  { label: "Waiting Area", category: "Building Facilities", icon: <Chair /> },
  { label: "Basement Parking", category: "Building Facilities", icon: <DirectionsCar /> },
  { label: "Maintenance", category: "Building Facilities", icon: <Build /> },
  { label: "Housekeeping", category: "Building Facilities", icon: <CleaningServices /> },
  { label: "Service Lift", category: "Building Facilities", icon: <Elevator /> },
  { label: "Common Terrace", category: "Building Facilities", icon: <Deck /> },
  // Family & Convenience
  { label: "Kids Play Area", category: "Family & Convenience", icon: <ChildCare /> },
  { label: "Creche/Day Care", category: "Family & Convenience", icon: <ChildFriendly /> },
  { label: "Senior Citizen Area", category: "Family & Convenience", icon: <Park /> },
  { label: "Convenience Store", category: "Family & Convenience", icon: <Storefront /> },
  { label: "Pharmacy", category: "Family & Convenience", icon: <LocalPharmacy /> },
  { label: "ATM", category: "Family & Convenience", icon: <LocalAtm /> },
  { label: "Salon", category: "Family & Convenience", icon: <ContentCut /> },
  { label: "Laundry", category: "Family & Convenience", icon: <LocalLaundryService /> },
  { label: "Medical Centre", category: "Family & Convenience", icon: <LocalHospital /> },
];

const amenityCategories = [
  "All",
  "Recreation",
  "Fitness & Wellness",
  "Sports",
  "Security",
  "Utilities",
  "Building Facilities",
  "Family & Convenience",
];

const getHighlightKey = (item) => `${item.label}|${item.subtitle || ""}`;
const getAmenityKey = (item) => `${item.category}|${item.label}`;

const ICON_KEYS = {
  "Swimming Pool": "pool",
  Gym: "gym",
  "Gated Community": "gate",
  "Near Metro": "metro",
};

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const getIconKey = (label) => ICON_KEYS[label] || slugify(label);

// ============================================================
// ✅ Validation helpers
// ============================================================
// DOM id used to scroll to a field
const fieldId = (key) => `field-${slugify(String(key))}`;

const LOCATION_ERROR_MSG = 'Please search and confirm your location (tap the ✓ button on the map)';

// ============================================================
// Helpers
// ============================================================
const convertBudgetToNumber = (budgetStr) => {
  if (!budgetStr || budgetStr === 'No Min') return 0;
  if (budgetStr.includes('K')) return parseFloat(budgetStr) * 1000;
  if (budgetStr.includes('L')) return parseFloat(budgetStr) * 100000;
  if (budgetStr.includes('CR')) return parseFloat(budgetStr) * 10000000;
  if (budgetStr.includes('+++')) return parseFloat(budgetStr) * 10000000;
  return parseFloat(budgetStr);
};

const centerDefault = { lat: 17.3850, lng: 78.4867 };

// ============================================================
// Component
// ============================================================
const BuilderRequest = () => { 
  const navigate = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [lookingFor, setLookingFor] = useState('');
  const [locationDetails, setLocationDetails] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const [location, setLocation] = useState(centerDefault);
  const [address, setAddress] = useState("");
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLEMAPS_API_KEY,
    libraries: ["places"],
  });

  const [propertyType, setPropertyType] = useState('');
  const [options, setOptions] = useState([]);
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [postedByOptions, setPostedByOptions] = useState([]);
  const [uploadLimits, setUploadLimits] = useState(null);
  const [subscriptionModal, setSubscriptionModal] = useState({ open: false, type: null });
  const [loading, setLoading] = useState(false);
  const [workPhotos, setWorkPhotos] = useState([]);

  // ✅ Validation errors: { [fieldKey]: 'message' }
  const [errors, setErrors] = useState({});

  const clearError = (key) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  // Amenity category tab state
  const [selectedAmenityCategory, setSelectedAmenityCategory] = useState("All");

  useEffect(() => {
    if (isGuest) setShowGuestModal(true);
  }, [isGuest]);

  // Clear image error automatically once an image is added
  useEffect(() => {
    if (workPhotos.length > 0) clearError('images');
  }, [workPhotos.length]);

  // Clear location error once an address exists AND the pin is confirmed
  useEffect(() => {
    if (address && locationConfirmed) clearError('location');
  }, [address, locationConfirmed]);

  // ============================================================
  // Image handlers — preview grid
  // ============================================================
  const handleWorkPhotosChange = (e) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (newFiles.length !== e.target.files.length) {
      alert("Please select image files only");
      e.target.value = "";
      return;
    }

    setWorkPhotos((prev) => {
      const merged = [...prev, ...newFiles];
      if (merged.length > 4) {
        alert("You can upload a maximum of 4 images only");
        return merged.slice(0, 4);
      }
      return merged;
    });

    e.target.value = "";
  };

  const getSelectedBudgetRange = (type, label) => {
    if (!type || !label) return null;

    return (
      BUDGET_RANGES[type]?.find(
        (range) => range.label === label
      ) || null
    );
  };

  const handleRemovePhoto = (index) => {
    setWorkPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  // ============================================================
  // Upload permissions
  // ============================================================
  useEffect(() => {
    const loadUploadPermissions = async () => {
      const result = await checkPropertyUploadPermission(userId);

      if (!result.allowed) {
        setSubscriptionModal({
          open: true,
          type: result.message?.toLowerCase().includes("subscription")
            ? "NO_SUBSCRIPTION"
            : "LIMIT_EXCEEDED",
        });
        return;
      }

      setUploadLimits(result.availability);

      const options = [];
      if (result.availability.Agent?.allowed) options.push("Agent");
      if (result.availability["Individual Owner/Builder"]?.allowed) {
        options.push("Owner", "Builder");
      }
      setPostedByOptions(options);
    };

    if (userId) loadUploadPermissions();
  }, [userId]);

  // ============================================================
  // Google Maps script fallback
  // ============================================================
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleRemoveImage = (indexToRemove) => {
    setWorkPhotos((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const getAvailableMaxBudgets = () => {
    if (!lookingFor || !minBudget) {
      return BUDGET_RANGES[lookingFor] || [];
    }

    const allBudgets = BUDGET_RANGES[lookingFor] || [];

    const minIndex = allBudgets.findIndex(
      (budget) => budget.label === minBudget
    );

    if (minIndex === -1) {
      return allBudgets;
    }

    return allBudgets.slice(minIndex + 1);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setUsingCurrentLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
          setLocation(pos);
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pos.lat},${pos.lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.status == 'OK' && data.results.length > 0) {
              setAddress(data.results[0].formatted_address);
            } else {
              setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
            }
          } catch (error) {
            console.error('Google Maps API error:', error);
            setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
          }
        },
        (error) => {
          console.error("Error getting current location:", error);
          alert("Error getting current location. Please enable location services.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        setUsingCurrentLocation(false);
        const newLoc = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setLocation(newLoc);
        setAddress(place.formatted_address);
      }
    }
  };

  const geocodeAddress = async () => {
    if (!address) return;
    if (usingCurrentLocation) return;
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status == 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setLocation({ lat: location.lat(), lng: location.lng() });
          setAddress(results[0].formatted_address);
        }
      });
    }
  };

  // ============================================================

  const handleFormDataChange = (field, value) => {
    if (NUMERIC_ONLY_FIELDS.includes(field)) {
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    }

    if (RATIO_FIELDS.includes(field)) {
      const digits = value.replace(/\D/g, '').slice(0, 4);

      if (digits.length >= 2) {
        value = `${digits.slice(0, 2)}:${digits.slice(2)}`;
      } else {
        value = digits;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLookingForChange = (value) => {
    setLookingFor(value);
    setPropertyType('');
    setMinBudget('');
    setMaxBudget('');
    setFormData(INITIAL_FORM_DATA);
    setSelectedAmenityCategory("All");
    // Property type / budget errors no longer apply after switching the type
    setErrors((prev) => {
      const { propertyType: _p, minBudget: _min, maxBudget: _max, ...rest } = prev;
      return rest;
    });
  };

  // ============================================================
  // Highlights / Amenities toggles
  // ============================================================
  const toggleHighlight = (key) => {
    setFormData((prev) => {
      const isSelected = prev.highlights.includes(key);
      const updatedHighlights = isSelected
        ? prev.highlights.filter((h) => h !== key)
        : [...prev.highlights, key];
      return { ...prev, highlights: updatedHighlights };
    });
  };

  const toggleAmenity = (key) => {
    setFormData((prev) => {
      const isSelected = prev.amenities.includes(key);
      const updatedAmenities = isSelected
        ? prev.amenities.filter((a) => a !== key)
        : [...prev.amenities, key];
      return { ...prev, amenities: updatedAmenities };
    });
  };

  // ============================================================
  // ✅ Validation + scroll to first error
  // Errors are inserted in on-screen order, so the first key is
  // always the top-most invalid field.
  // ============================================================
  const validateForm = () => {
    const newErrors = {};

    if (!lookingFor) newErrors.lookingFor = "Please select a type";
    if (!propertyType) newErrors.propertyType = "Please select a property type";

    // Location (remove the `locationConfirmed` check if you don't want to force the pin confirm)
    if (!address.trim() || !locationConfirmed) newErrors.location = LOCATION_ERROR_MSG;

    if (!minBudget) newErrors.minBudget = "Please select a min budget";
    if (!maxBudget) newErrors.maxBudget = "Please select a max budget";

    if (!formData.posted_by) newErrors.posted_by = "Please select who is posting";

    if (workPhotos.length === 0) newErrors.images = "Please upload at least 1 image";

    return newErrors;
  };

  const scrollToFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    const el = document.getElementById(fieldId(firstKey));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      el.querySelector('input, textarea, [role="combobox"], [role="button"]')?.focus({ preventScroll: true });
    }, 400);
  };

  const handleSave = async () => {

    // ✅ Run validation, show red messages and scroll to the first invalid field
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    const selectedMinBudget = getSelectedBudgetRange(
      lookingFor,
      minBudget
    );

    const selectedMaxBudget = getSelectedBudgetRange(
      lookingFor,
      maxBudget
    );

    if (workPhotos.length > 4) {
      alert("You can upload a maximum of 4 images only");
      return;
    }

    if (!uploadLimits) {
      setSubscriptionModal({ open: true, type: "NO_SUBSCRIPTION" });
      return;
    }

    const roleKey =
      formData.posted_by === "Agent"
        ? "Agent"
        : "Individual Owner/Builder";

    if (!uploadLimits[roleKey]?.allowed) {
      setSubscriptionModal({ open: true, type: "LIMIT_EXCEEDED" });
      return;
    }

    const permission = await checkPropertyUploadPermission(userId);
    if (!permission.allowed) {
      alert(permission.message);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        type: lookingFor.toLowerCase(),
        property_type: propertyType,

        length: parseFloat(formData.length) || 0,
        width: parseFloat(formData.width) || 0,
        units: formData.units,

        min_budget: selectedMinBudget?.min || 0,
        max_budget: selectedMaxBudget?.max || 0,
  landmark: formData.landmark, // NEW
        min_acres: parseFloat(formData.min_acres) || 0,
        max_acres: parseFloat(formData.max_acres) || 0,

        ratio: formData.ratio,

        lat: location.lat,
        long: location.lng,
        location: address,

        floor: parseInt(formData.floor) || 0,

        comment: "Property request from form",

        user_id: userId,

        posted_by: formData.posted_by,

        roadwidth: parseFloat(formData.roadwidth) || 0,

        facing: formData.facing,

        description: formData.description,
      };

      const formDataToSend = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        formDataToSend.append(key, value ?? "");
      });

      // ✅ Property Highlights
      const highlightPayload = formData.highlights
        .map((key) => highlightOptions.find((o) => getHighlightKey(o) === key))
        .filter(Boolean)
        .map((item, index) => ({
          name: item.label,
          icon: getIconKey(item.label),
          order: index + 1,
          is_active: true,
        }));
      formDataToSend.append("property_highlights", JSON.stringify(highlightPayload));
      formDataToSend.append("highlights", JSON.stringify(highlightPayload));

      // ✅ Property Amenities (deduped by name)
      const seenAmenities = new Set();
      const amenityPayload = formData.amenities
        .map((key) => amenitiesOptions.find((o) => getAmenityKey(o) === key))
        .filter(
          (item) =>
            item && !seenAmenities.has(item.label) && seenAmenities.add(item.label)
        )
        .map((item, index) => ({
          name: item.label,
          icon: getIconKey(item.label),
          order: index + 1,
          is_active: true,
        }));
      formDataToSend.append("property_amenities", JSON.stringify(amenityPayload));
      formDataToSend.append("amenities", JSON.stringify(amenityPayload));

      // Images
      workPhotos.forEach((file) => {
        formDataToSend.append("new_property_images", file);
      });

      const response = await fetch(`${BASE_URL}/property/`, {
        method: "POST",
        body: formDataToSend,
      });

      if (response.ok) {
        setAlertMessage("Property request saved successfully!");
        setShowSuccess(true);
      } else {
        const errorData = await response.json().catch(() => null);
        setAlertMessage(errorData?.message || "Failed to save property request");
        setShowFailure(true);
      }
    } catch (error) {
      console.error("Error saving property:", error);
      setAlertMessage("Error while saving property");
      setShowFailure(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);
  const handleBackClick = () => navigate(-1);

  // ============================================================
  // Field renderers
  // ============================================================
  const renderDimensionFields = () => (
    <Box key="dimensions" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Dimensions</Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth label="Length" placeholder="e.g., 40" variant="outlined" size="small"
          value={formData.length}
          onChange={(e) => handleFormDataChange('length', e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
        />
        <Typography sx={{ alignSelf: 'center' }}>×</Typography>
        <TextField
          fullWidth label="Width" placeholder="e.g., 60" variant="outlined" size="small"
          value={formData.width}
          onChange={(e) => handleFormDataChange('width', e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
        />
        <TextField select label="Units" value={formData.units} size="small" sx={{ minWidth: 80 }}
          onChange={(e) => handleFormDataChange('units', e.target.value)}>
          <MenuItem value="ft">ft</MenuItem>
          <MenuItem value="m">m</MenuItem>
        </TextField>
      </Stack>
    </Box>
  );

  const renderRangeFields = (minField, maxField, label) => (
    <Box key={label} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{label}</Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth label={`Min ${label}`} placeholder="e.g., 10" variant="outlined" size="small"
          value={formData[minField]}
          onChange={(e) => handleFormDataChange(minField, e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
        />
        <TextField
          fullWidth label={`Max ${label}`} placeholder="e.g., 15" variant="outlined" size="small"
          value={formData[maxField]}
          onChange={(e) => handleFormDataChange(maxField, e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
        />
      </Stack>
    </Box>
  );

  const renderBedroomField = () => (
    <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
      <InputLabel>Number of Bedrooms</InputLabel>
      <Select label="Number of Bedrooms" value={formData.no_of_bedrooms}
        onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}>
        {[1, 2, 3, 4].map(num => <MenuItem key={num} value={num.toString()}>{num} BHK</MenuItem>)}
      </Select>
    </FormControl>
  );

  const renderTextField = (field, label, placeholder) => (
    <TextField
      key={field}
      fullWidth
      label={label}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      sx={{ mb: 2 }}
      value={formData[field]}
      onChange={(e) => handleFormDataChange(field, e.target.value)}
      onKeyDown={(e) => {
        if (
          RATIO_FIELDS.includes(field) &&
          e.key === 'Backspace' &&
          e.target.selectionStart === 3 &&
          e.target.selectionEnd === 3
        ) {
          e.preventDefault();

          const currentValue = formData[field] || '';
          const digits = currentValue.replace(/\D/g, '');

          const updatedDigits = digits.slice(0, 1) + digits.slice(2);

          handleFormDataChange(field, updatedDigits);
        }
      }}
      inputProps={
        RATIO_FIELDS.includes(field)
          ? {
              maxLength: 5,
              inputMode: 'numeric',
            }
          : NUMERIC_ONLY_FIELDS.includes(field)
          ? {
              inputMode: 'decimal',
              pattern: '[0-9]*',
            }
          : {}
      }
    />
  );

  const getDynamicFields = () => {
    if (!propertyType || !lookingFor) return null;

    const effectivePropertyType =
      ['JV/JD', 'Build to Suit'].includes(lookingFor) && propertyType !== 'Land'
        ? 'Plot'
        : propertyType;

    const fields = [];
    const config = {
      Purchase: {
        dimensions: ['Plot', 'Rental with Duplex Building', 'Duplex House', 'Land', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        acres: ['Land'],
        budgetPerAcre: ['Land'],
        rentalIncome: ['PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        bedrooms: ['Flat', 'Villas']
      },
      Rent: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        monthlyRent: ['*'],
        dimensions: ['Commercial Plot', 'Commercial Building', 'Commercial Land', 'PG', 'Office', 'Flat'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      Lease: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        leasePerYear: ['*'],
        dimensions: ['Plot', 'Commercial Building', 'Commercial Plot', 'Commercial Land', 'PG', 'Office', 'Flat'],
        monthlyRent: ['Commercial Plot', 'Commercial Land'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      'JV/JD': { acres: ['Land'], dimensions: ['Plot'], ratio: ['*'] },
      'Build to Suit': { acres: ['Land'], dimensions: ['Plot'], floor: ['Plot'] }
    };

    const currentConfig = config[lookingFor];
    if (!currentConfig) return fields;

    Object.entries(currentConfig).forEach(([fieldType, propertyTypes]) => {
      if (propertyTypes.includes('*') || propertyTypes.includes(effectivePropertyType)) {
        switch (fieldType) {
          case 'dimensions': fields.push(renderDimensionFields()); break;
          case 'acres': fields.push(renderRangeFields('min_acres', 'max_acres', 'Acres')); break;
          case 'monthlyRent': fields.push(renderRangeFields('min_monthly_rent', 'max_monthly_rent', 'Monthly Rent')); break;
          case 'leasePerYear': fields.push(renderRangeFields('min_lease_per_year', 'max_lease_per_year', 'Lease per Year')); break;
          case 'budgetPerAcre': fields.push(renderTextField('budget_per_acre', 'Budget per Acre', 'e.g., 500000')); break;
          case 'rentalIncome': fields.push(renderTextField('expected_rental_income', 'Expected Rental Income', 'e.g., 50000')); break;
          case 'bedrooms': fields.push(renderBedroomField()); break;
          case 'ratio': fields.push(renderTextField('ratio', 'Ratio (Owner:Developer)', 'e.g., 60:40')); break;
          case 'floor': fields.push(renderTextField('floor', 'Floor', 'e.g., 3')); break;
          case 'area': fields.push(renderTextField('area', 'Area (sq ft)', 'e.g., 1200')); break;
          default: break;
        }
      }
    });

    return fields;
  };

  // ============================================================
  // Highlights + Amenities section
  // ============================================================
  const renderHighlightsAndAmenities = () => (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#3D2B1F' }}>
        Property Highlights
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          mb: 4,
        }}
      >
        {highlightOptions.map((item) => {
          const itemKey = getHighlightKey(item);
          const isSelected = formData.highlights.includes(itemKey);
          return (
            <Box
              key={itemKey}
              onClick={() => toggleHighlight(itemKey)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 0.5,
                p: 1,
                borderRadius: '10px',
                border: `1px solid ${isSelected ? '#9a4f20' : '#E0E0E0'}`,
                backgroundColor: isSelected ? '#F5EDE4' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minHeight: '90px',
                justifyContent: 'center',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: isSelected ? '#EDE0D4' : '#FAFAFA',
                  borderColor: isSelected ? '#9a4f20' : '#B0B0B0',
                },
              }}
            >
              {isSelected && (
                <CheckCircle
                  sx={{ position: 'absolute', top: 4, right: 4, fontSize: '14px', color: '#9a4f20' }}
                />
              )}
              <Box sx={{ color: '#9a4f20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', mb: 0.5 }}>
                {item.icon}
              </Box>
              <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, wordBreak: 'break-word' }}>
                {item.label}
              </Typography>
              {item.subtitle && (
                <Typography sx={{ fontSize: '9px', fontWeight: 400, color: '#888', lineHeight: 1.2, wordBreak: 'break-word' }}>
                  {item.subtitle}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#3D2B1F' }}>
        Amenities
      </Typography>

      {/* Category Tabs */}
      <Box
        sx={{
          mb: 2,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #E0E0E0',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Stack direction="row" spacing={0} sx={{ display: 'inline-flex' }}>
          {amenityCategories.map((cat) => (
            <Box
              key={cat}
              onClick={() => setSelectedAmenityCategory(cat)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                borderBottom:
                  selectedAmenityCategory === cat
                    ? '2px solid #9a4f20'
                    : '2px solid transparent',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: selectedAmenityCategory === cat ? 600 : 500,
                  color: selectedAmenityCategory === cat ? '#9a4f20' : '#666',
                  whiteSpace: 'nowrap',
                }}
              >
                {cat}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Amenities Grid */}
      {(selectedAmenityCategory === 'All'
        ? amenityCategories.filter((c) => c !== 'All')
        : [selectedAmenityCategory]
      ).map((cat) => {
        const items = amenitiesOptions.filter((a) => a.category === cat);
        if (items.length === 0) return null;

        return (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#333', mb: 1.5 }}>
              {cat}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
              }}
            >
              {items.map((item) => {
                const itemKey = getAmenityKey(item);
                const isSelected = formData.amenities.includes(itemKey);
                return (
                  <Box
                    key={itemKey}
                    onClick={() => toggleAmenity(itemKey)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 0.5,
                      p: 1,
                      borderRadius: '10px',
                      border: `1px solid ${isSelected ? '#9a4f20' : '#E0E0E0'}`,
                      backgroundColor: isSelected ? '#F5EDE4' : '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      minHeight: '74px',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      '&:hover': {
                        backgroundColor: isSelected ? '#EDE0D4' : '#FAFAFA',
                        borderColor: isSelected ? '#9a4f20' : '#B0B0B0',
                      },
                    }}
                  >
                    {isSelected && (
                      <CheckCircle
                        sx={{ position: 'absolute', top: 4, right: 4, fontSize: '13px', color: '#9a4f20' }}
                      />
                    )}
                    <Box sx={{ color: '#9a4f20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: '9.5px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, wordBreak: 'break-word' }}>
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // ============================================================
  // Render
  // ============================================================
  return (
    <>
      <SearchBar onBackClick={handleBackClick} />
      <Box
        sx={{
          padding: '16px',
          backgroundColor: '#f5f1eb',
          minHeight: '100vh',
          height: '100%',
          marginTop: '50px',
          paddingBottom: '120px',
          overflowY: 'auto',
        }}
      >
        <FilterContainer>
          <SectionTitle>Looking For</SectionTitle>
          <FormControl
            fullWidth
            sx={{ mb: 2 }}
            size="small"
            id={fieldId('lookingFor')}
            error={!!errors.lookingFor}
          >
            <InputLabel
              id="looking-for-label"
              sx={{ color: '#8B6914', '&.Mui-focused': { color: '#8B6914' } }}
            >
              Select Type *
            </InputLabel>
            <Select
              labelId="looking-for-label"
              value={lookingFor}
              label="Select Type *"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#8B6914' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6B4F12' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8B6914', borderWidth: '2px' },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' },
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    '& .MuiMenuItem-root': { display: 'flex', alignItems: 'center', gap: 1.5, padding: '2px 16px' },
                    '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#F5EDE4', '&:hover': { backgroundColor: '#E8DDD0' } },
                    '& .MuiMenuItem-root:hover': { backgroundColor: '#F5EDE4' },
                  },
                },
              }}
              onChange={(e) => {
                handleLookingForChange(e.target.value);
                clearError('lookingFor');
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selected && categoryIcons[selected]}
                  <Typography sx={{ color: '#3D2B1F' }}>{selected || 'Select Type'}</Typography>
                </Box>
              )}
            >
              {LOOKING_FOR_OPTIONS.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, padding: '8px 16px',
                    '&:hover': { backgroundColor: '#F5EDE4 !important' },
                    '&.Mui-selected': { backgroundColor: '#E8DDD0 !important', '&:hover': { backgroundColor: '#E8DDD0 !important' } },
                  }}
                >
                  <Box sx={{ minWidth: 30 }}>
                    {categoryIcons[option] || <Home sx={{ color: '#8B6914', fontSize: 20 }} />}
                  </Box>
                  <Typography sx={{ color: '#3D2B1F', fontWeight: 500 }}>{option}</Typography>
                </MenuItem>
              ))}
            </Select>
            {errors.lookingFor && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                {errors.lookingFor}
              </Typography>
            )}
          </FormControl>

          <SectionTitle>Property Type</SectionTitle>
          <FormControl
            fullWidth
            sx={{ mb: 2 }}
            size="small"
            id={fieldId('propertyType')}
            error={!!errors.propertyType}
          >
            <InputLabel
              id="property-type-label"
              sx={{ color: '#8B6914', '&.Mui-focused': { color: '#8B6914' } }}
            >
              Select Property Type *
            </InputLabel>
            <Select
              labelId="property-type-label"
              value={propertyType}
              label="Select Property Type *"
              disabled={!lookingFor}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#8B6914' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6B4F12' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8B6914', borderWidth: '2px' },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' },
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    '& .MuiMenuItem-root': { display: 'flex', alignItems: 'center', gap: 1.5, padding: '2px 16px' },
                    '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#F5EDE4', '&:hover': { backgroundColor: '#E8DDD0' } },
                    '& .MuiMenuItem-root:hover': { backgroundColor: '#F5EDE4' },
                  },
                },
              }}
              onChange={(e) => {
                setPropertyType(e.target.value);
                clearError('propertyType');
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selected && categoryIcons[selected]}
                  <Typography sx={{ color: '#3D2B1F' }}>{selected || 'Select Property Type'}</Typography>
                </Box>
              )}
            >
              {lookingFor && PROPERTY_TYPE_OPTIONS[lookingFor]?.map((type) => (
                <MenuItem
                  key={type}
                  value={type}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, padding: '8px 16px',
                    '&:hover': { backgroundColor: '#F5EDE4 !important' },
                    '&.Mui-selected': { backgroundColor: '#E8DDD0 !important', '&:hover': { backgroundColor: '#E8DDD0 !important' } },
                  }}
                >
                  <Box sx={{ minWidth: 30 }}>
                    {categoryIcons[type] || <Home sx={{ color: '#8B6914', fontSize: 20 }} />}
                  </Box>
                  <Typography sx={{ color: '#3D2B1F', fontWeight: 500 }}>{type}</Typography>
                </MenuItem>
              ))}
            </Select>
            {errors.propertyType && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                {errors.propertyType}
              </Typography>
            )}
          </FormControl>

{/* Property Nearest Landmark */}
<Box sx={{ mb: 2 }}>
  <TextField
    fullWidth
    size="small"
    label="Property Nearest Landmark"
    placeholder="Enter nearest landmark"
    variant="outlined"
    value={formData.landmark || ''}
    onChange={(e) =>
      handleFormDataChange('landmark', e.target.value)
    }
  />
</Box>

          {/* Location */}
          <Box sx={{ mb: 2 }} id={fieldId('location')}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Location (Pin point property on map)
            </Typography>

            <Autocomplete
              onLoad={(autocomplete) => { autocompleteRef.current = autocomplete; }}
              onPlaceChanged={onPlaceChanged}
            >
              <TextField
                fullWidth
                label="Search Location *"
                variant="outlined"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onBlur={geocodeAddress}
                error={!!errors.location}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={getCurrentLocation} edge="end">
                        <MyLocationIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Autocomplete>

            <div style={{ ...containerStyle, position: 'relative' }}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={location}
                zoom={15}
                options={{ gestureHandling: 'greedy' }}
                onLoad={(map) => { mapRef.current = map; }}
                onDragStart={() => setLocationConfirmed(false)}
              />

              <div
                style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -100%)',
                  pointerEvents: 'none', zIndex: 10,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
                  <path fill="#ff0000" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (window.google && window.google.maps && mapRef.current) {
                    const center = mapRef.current.getCenter();
                    const lat = center.lat();
                    const lng = center.lng();
                    setLocation({ lat, lng });

                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                      if (status === 'OK' && results[0]) {
                        const selectedAddress = results[0].formatted_address;
                        setAddress(selectedAddress);
                        setFormData(prev => ({ ...prev, location: selectedAddress, lat: lat.toString(), long: lng.toString() }));
                      } else {
                        const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
                        setAddress(fallback);
                        setFormData(prev => ({ ...prev, location: fallback, lat: lat.toString(), long: lng.toString() }));
                      }
                      setLocationConfirmed(true);
                    });
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: locationConfirmed ? 'green' : 'red',
                  border: 'none', borderRadius: '50%',
                  width: '44px', height: '44px', cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  position: 'absolute', right: '10px', top: '45%',
                  transform: 'translateY(-50%)', zIndex: 10,
                  transition: 'background-color 0.3s ease',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                  <path fill="white" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </button>
            </div>

            {errors.location && (
              <Typography
                variant="caption"
                sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}
              >
                {errors.location}
              </Typography>
            )}
          </Box>

          {/* Budget Range */}
          <SectionTitle sx={{ mt: 2 }}>
            Budget Range per Acre/Sft
          </SectionTitle>

          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">

              {/* MIN BUDGET */}
              <FormControl
                fullWidth
                size="small"
                id={fieldId('minBudget')}
                error={!!errors.minBudget}
              >
                <InputLabel>Min Budget *</InputLabel>

                <Select
                  value={minBudget}
                  label="Min Budget *"
                  onChange={(e) => {
                    setMinBudget(e.target.value);

                    // Reset max whenever minimum changes
                    setMaxBudget("");

                    clearError('minBudget');
                    clearError('maxBudget');
                  }}
                  disabled={!lookingFor}
                >
                  {lookingFor &&
                    BUDGET_RANGES[lookingFor]?.map((budget) => (
                      <MenuItem
                        key={budget.label}
                        value={budget.label}
                      >
                        {budget.label}
                      </MenuItem>
                    ))}
                </Select>

                {errors.minBudget && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      ml: 1.5,
                    }}
                  >
                    {errors.minBudget}
                  </Typography>
                )}
              </FormControl>

              {/* MAX BUDGET */}
              <FormControl
                fullWidth
                size="small"
                id={fieldId('maxBudget')}
                error={!!errors.maxBudget}
              >
                <InputLabel>Max Budget *</InputLabel>

                <Select
                  value={maxBudget}
                  label="Max Budget *"
                  onChange={(e) => {
                    setMaxBudget(e.target.value);
                    clearError('maxBudget');
                  }}
                  disabled={!lookingFor || !minBudget}
                >
                  {getAvailableMaxBudgets().map((budget) => (
                    <MenuItem
                      key={budget.label}
                      value={budget.label}
                    >
                      {budget.label}
                    </MenuItem>
                  ))}
                </Select>

                {errors.maxBudget && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      ml: 1.5,
                    }}
                  >
                    {errors.maxBudget}
                  </Typography>
                )}
              </FormControl>

            </Stack>
          </Box>

          {getDynamicFields()}

          {/* Posted By */}
          <FormControl
            fullWidth
            size="small"
            id={fieldId('posted_by')}
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 },
              '& .MuiSelect-select': { py: 1.5 },
            }}
            error={!!errors.posted_by}
          >
            <InputLabel>Posted By *</InputLabel>
            <Select
              value={formData.posted_by}
              onChange={(e) => {
                handleFormDataChange("posted_by", e.target.value);
                clearError('posted_by');
              }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 200, width: 'auto', minWidth: 150 } },
              }}
            >
              {postedByOptions.map((role) => (
                <MenuItem key={role} value={role} sx={{ py: 1 }}>{role}</MenuItem>
              ))}
            </Select>
            {errors.posted_by && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                {errors.posted_by}
              </Typography>
            )}
          </FormControl>

          {/* Road Width */}
          <TextField
            fullWidth size="small" label="Road Width (ft)" sx={{ mb: 2 }}
            value={formData.roadwidth}
            onChange={(e) => handleFormDataChange('roadwidth', e.target.value)}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          />

          {/* Facing */}
          <FormControl
            fullWidth size="small"
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 },
              '& .MuiSelect-select': { py: 1.5 },
            }}
          >
            <InputLabel>Facing</InputLabel>
            <Select
              value={formData.facing}
              onChange={(e) => handleFormDataChange('facing', e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200, width: 'auto', minWidth: 150,
                    '& .MuiMenuItem-root': { padding: '10px 16px' },
                    '& .MuiMenuItem-root:hover': { backgroundColor: '#F5EDE4' },
                    '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#E8DDD0' },
                  },
                },
              }}
            >
              {['East','West','North','South','North-East','North-West','South-East','South-West'].map((v) => (
                <MenuItem key={v} value={v} sx={{ py: 1 }}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FullScreenLoader open={loading} text="Saving changes..." />

          {/* Image Upload */}
          <Box id={fieldId('images')}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                mt: 3,
                color: '#3D2B1F',
              }}
            >
              Uploaded Images <span style={{ color: 'red' }}>*</span>
            </Typography>

            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon sx={{ color: '#8B6914' }} />}
              fullWidth
              disabled={workPhotos.length >= 4}
              sx={{
                mb: 1,
                borderColor: errors.images || workPhotos.length === 0 ? '#d32f2f' : '#8B6914',
                color: '#8B6914',
                '&:hover': {
                  borderColor: '#6B4F12',
                  backgroundColor: 'rgba(139, 105, 20, 0.04)',
                },
                '& .MuiButton-startIcon': {
                  color: '#8B6914',
                },
                '&.Mui-disabled': {
                  borderColor: '#D3C7A8',
                  color: '#B8AC8E',
                },
              }}
            >
              {workPhotos.length >= 4
                ? 'Maximum 4 images selected'
                : 'Upload Images'}

              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                multiple
                onChange={handleWorkPhotosChange}
              />
            </Button>

            <Typography
              variant="caption"
              sx={{
                color: errors.images || workPhotos.length === 0 ? '#d32f2f' : '#8B6914',
                display: 'block',
                mb: 1,
              }}
            >
              {errors.images
                ? errors.images
                : workPhotos.length === 0
                  ? 'Please upload at least 1 image'
                  : `Max 4 images allowed (${workPhotos.length}/4 selected)`}
            </Typography>
          </Box>

          {workPhotos.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                mt: 2,
                mb: 2,
              }}
            >
              {workPhotos.map((file, index) => {
                const previewUrl = URL.createObjectURL(file);

                return (
                  <Box
                    key={`${file.name}-${index}`}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #E0D5C0',
                      backgroundColor: '#FAF5EF',
                    }}
                  >
                    <Box
                      component="img"
                      src={previewUrl}
                      alt={file.name}
                      onLoad={() => URL.revokeObjectURL(previewUrl)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: 'white',
                        width: 22,
                        height: 22,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.8)',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ============================================================ */}
          {/* Highlights + Amenities                                       */}
          {/* ============================================================ */}
          {renderHighlightsAndAmenities()}

          {/* Description */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", color: "#3D2B1F" }}>
              Description
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              inputProps={{ maxLength: 1500 }}
              name="description"
              value={formData.description || ""}
              onChange={(e) => handleFormDataChange("description", e.target.value)}
              placeholder="Enter property description..."
              disabled={loading || isGuest}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" sx={{ color: "#8B6914" }}>
              {(formData.description || "").length}/1500
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              disabled={loading}
              sx={{
                borderRadius: "8px",
                borderColor: "#8B4513",
                color: "#8B4513",
                "&:hover": { borderColor: "#654321", backgroundColor: "rgba(139, 69, 19, 0.04)" },
              }}
            >
              Cancel
            </Button>

            <StyledButton
              fullWidth
              onClick={handleSave}
              disabled={isGuest || loading}
              sx={{
                backgroundColor: (isGuest || loading) ? "#ccc" : undefined,
                color: (isGuest || loading) ? "#777" : undefined,
                cursor: (isGuest || loading) ? "not-allowed" : "pointer",
                "&:hover": { backgroundColor: (isGuest || loading) ? "#ccc" : undefined },
              }}
            >
              {isGuest ? "Login Required" : "Save"}
            </StyledButton>
          </Stack>
        </FilterContainer>
      </Box>

      <SubscriptionModal
        open={subscriptionModal.open}
        type={subscriptionModal.type}
        onClose={() => setSubscriptionModal({ open: false, type: null })}
      />

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => { setShowGuestModal(false); }}
      />

      {showSuccess && (
        <SuccessAlert
          message={alertMessage}
          onClose={() => { setShowSuccess(false); navigate("/post"); }}
        />
      )}

      {showFailure && (
        <FailureAlert
          message={alertMessage}
          onClose={() => {
            setShowFailure(false);
            setAlertMessage("");
            navigate("/builder-registration-form");
          }}
        />
      )}

      <FormsBottomNavbar />
    </>
  );
};

export default BuilderRequest;